import { useEffect, useMemo } from 'react'
import zipcodes from 'zipcodes'
import { useGetPublicEnrollment, useSessionNearbyLocations } from '@/api/enrollment-api'
import { useEnrollmentStore } from '@/stores'
import { getEnrollmentString } from '../../utils/enrollmentHydration/helpers'
import { clearEnrollmentBrowserState } from '../../state/enrollmentBrowserStorage'
import {
  findTrainingLocation,
  formatConfirmedStartDate,
  formatTrainingLocation
} from './helpers'

function clearLocalStorageKeepEmail() {
  clearEnrollmentBrowserState()
}

export function useEnrollmentDetailsData() {
  const sessionToken = useEnrollmentStore(state => state.sessionToken)
  const enrollmentQuery = useGetPublicEnrollment(sessionToken)
  const enrollment = enrollmentQuery.data
  const zipCode = getEnrollmentString(enrollment ?? null, ['zipCode'])
  const zipInfo = zipCode ? zipcodes.lookup(zipCode) : null
  const latitude = zipInfo?.latitude ?? null
  const longitude = zipInfo?.longitude ?? null
  const locationsQuery = useSessionNearbyLocations(
    latitude,
    longitude,
    sessionToken,
    enrollment?.sponsorCompanyId ?? null
  )
  const locations = locationsQuery.data ?? []

  useEffect(() => {
    clearLocalStorageKeepEmail()
  }, [])

  const summary = useMemo(() => {
    const locationExternalId =
      enrollment?.locationExternalId ??
      enrollment?.trainingLocationExternalId ??
      enrollment?.latestQuote?.locationExternalId ??
      null
    const locationName =
      enrollment?.locationName ?? enrollment?.latestQuote?.locationName ?? null
    const programName = enrollment?.programName ?? null
    const confirmedStartDate =
      getEnrollmentString(enrollment ?? null, ['confirmedStartDate']) || null
    const yardDays = enrollment?.yardDays ?? []
    const trainingLocation = findTrainingLocation(locations, locationExternalId)

    const formattedLocation = formatTrainingLocation(trainingLocation, locationName)
    const formattedSchedule =
      yardDays.length > 0 ? `${yardDays.join(', ')}\n8 AM - 4:30 PM` : '8 AM - 4:30 PM'

    return {
      programType: programName ?? '—',
      location: formattedLocation,
      startDate: formatConfirmedStartDate(confirmedStartDate),
      schedule: formattedSchedule
    }
  }, [enrollment, locations])

  const isLoading =
    enrollmentQuery.isPending ||
    (latitude !== null && longitude !== null && locationsQuery.isPending)

  return {
    data: summary,
    isLoading
  }
}
